import React, { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { getCategory, getUnit } from "../data/tools";
import { convertValue, formatNumber } from "../lib/convert";
import { ConverterWidget } from "../components/ConverterWidget";
import { AdSlot } from "../components/AdSlot";
import { SectionLabel } from "../components/SectionLabel";
import { usePageMeta } from "../lib/usePageMeta";
import { trackUsage } from "../lib/api";
import NotFound from "./NotFound";

const TABLE_VALUES = [1, 2, 3, 4, 5, 10, 20, 25, 50, 100, 500, 1000];

export default function ConverterPage() {
  const { categoryId, from, to } = useParams();
  const category = getCategory(categoryId);
  const fromUnit = getUnit(category, from);
  const toUnit = getUnit(category, to);

  const title = fromUnit && toUnit ? `Convert ${fromUnit.name} to ${toUnit.name}` : "Convert";
  usePageMeta(
    fromUnit && toUnit ? `${fromUnit.name} to ${toUnit.name} — Instant Converter | Utiliverse` : "Converter",
    fromUnit && toUnit ? `Convert ${fromUnit.name} (${fromUnit.symbol}) to ${toUnit.name} (${toUnit.symbol}) instantly with a free, accurate converter and reference table.` : ""
  );

  useEffect(() => {
    if (category && fromUnit && toUnit) {
      trackUsage({
        slug: `pair-${categoryId}-${from}-${to}`,
        title: `${fromUnit.name} → ${toUnit.name}`,
        path: `/convert/${categoryId}/${from}/${to}`,
        category: "conversion",
      });
    }
  }, [category, fromUnit, toUnit, categoryId, from, to]);

  const oneEquals = useMemo(
    () => (category && fromUnit && toUnit ? convertValue(category, from, to, 1) : NaN),
    [category, fromUnit, toUnit, from, to]
  );

  const related = useMemo(() => {
    if (!category) return [];
    return category.units
      .filter((u) => u.id !== from && u.id !== to)
      .slice(0, 6)
      .map((u) => ({ from, to: u.id, name: `${fromUnit.name} to ${u.name}` }));
  }, [category, from, to, fromUnit]);

  if (!category || !fromUnit || !toUnit) return <NotFound />;

  return (
    <div>
      <section className="bp-grid border-b-2 border-ink">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12">
          <nav className="font-mono text-xs text-ink/50 mb-4 flex flex-wrap gap-1">
            <Link to="/converters" className="hover:text-signal">Converters</Link> /
            <Link to={`/converters/${category.id}`} className="hover:text-signal">{category.label}</Link> /
            <span>{fromUnit.symbol}→{toUnit.symbol}</span>
          </nav>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none mb-3">
            {fromUnit.name} to {toUnit.name}
          </h1>
          <p className="font-mono text-lg text-ink/70 mb-8">
            1 {fromUnit.symbol} = <span className="text-signal font-bold">{formatNumber(oneEquals)} {toUnit.symbol}</span>
          </p>
          <ConverterWidget category={category} initialFrom={from} initialTo={to} />
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12">
        <AdSlot format="leaderboard" testid="pair-ad-top" />

        {/* Reference table */}
        <section className="mt-12">
          <SectionLabel n="◆">{fromUnit.name} → {toUnit.name} Table</SectionLabel>
          <div className="border-2 border-ink bg-paper overflow-hidden" data-testid="conversion-table">
            <div className="grid grid-cols-2 font-mono text-[11px] uppercase tracking-widest bg-ink text-paper">
              <div className="px-5 py-3 border-r border-paper/20">{fromUnit.name} ({fromUnit.symbol})</div>
              <div className="px-5 py-3">{toUnit.name} ({toUnit.symbol})</div>
            </div>
            {TABLE_VALUES.map((v, i) => (
              <div key={v} className={`grid grid-cols-2 font-mono text-sm border-t border-ink/10 ${i % 2 ? "bg-canvas" : ""}`}>
                <div className="px-5 py-2.5 border-r border-ink/10">{v} {fromUnit.symbol}</div>
                <div className="px-5 py-2.5 font-bold">{formatNumber(convertValue(category, from, to, v))} {toUnit.symbol}</div>
              </div>
            ))}
          </div>
        </section>

        {/* How-to / SEO content */}
        <section className="mt-12 prose max-w-none">
          <SectionLabel n="◆">How to convert {fromUnit.name} to {toUnit.name}</SectionLabel>
          <div className="border-2 border-ink bg-paper p-7 space-y-4">
            <p className="text-ink/80">
              To convert from <strong>{fromUnit.name.toLowerCase()}</strong> to <strong>{toUnit.name.toLowerCase()}</strong>,
              {category.special
                ? " apply the temperature formula for these scales — the converter above handles it automatically."
                : <> multiply the {fromUnit.name.toLowerCase()} value by <span className="font-mono bg-canvas px-1.5 py-0.5 border border-ink/20">{formatNumber(oneEquals)}</span>, since 1&nbsp;{fromUnit.symbol} equals {formatNumber(oneEquals)}&nbsp;{toUnit.symbol}.</>}
            </p>
            <p className="text-ink/80">
              For example, 10&nbsp;{fromUnit.symbol} is{" "}
              <strong>{formatNumber(convertValue(category, from, to, 10))}&nbsp;{toUnit.symbol}</strong>. Just type any value
              into the converter above to get an instant, precise result.
            </p>
          </div>
        </section>

        <div className="mt-12"><AdSlot format="inContent" testid="pair-ad-mid" /></div>

        {/* Related */}
        <section className="mt-12">
          <SectionLabel n="◆">Related Conversions</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border-t-2 border-l-2 border-ink">
            {related.map((r) => (
              <Link
                key={r.to}
                to={`/convert/${category.id}/${r.from}/${r.to}`}
                data-testid={`related-${r.to}`}
                className="border-b-2 border-r-2 border-ink bg-paper px-5 py-4 hover:bg-gold transition-colors text-sm font-medium"
              >
                {r.name}
              </Link>
            ))}
            <Link
              to={`/convert/${category.id}/${to}/${from}`}
              data-testid="related-reverse"
              className="border-b-2 border-r-2 border-ink bg-canvas px-5 py-4 hover:bg-gold transition-colors text-sm font-bold"
            >
              ↺ {toUnit.name} to {fromUnit.name}
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
